import React, { useEffect, useState, useCallback } from "react";
import styled, { withTheme } from "styled-components";
import LabelledInput from "../../ui-library/LabelledInput/LabelledInput";
import PercentageInput from "./Controls/PercentageInput";
import PropTypes from "prop-types";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.color || "transparent"};
  font-weight: 600 !important;
`;

const Title = styled.span`
  font-weight: 600;
  color: ${(props) => props.theme?.blackLite};
  font-size: 14px;
`;

const SetDailyGoals = (props) => {
  const { backgroundColor, validate, onValidate, editing, defaultTargets } =
    props;

  const [calories, setCalories] = useState("");
  const [proteinPercentage, setProteinPercentage] = useState(null);
  const [proteinGrams, setProteinGrams] = useState("");
  const [carbohydratesPercentage, setCarbohydratesPercentage] = useState(null);
  const [carbohydratesGrams, setCarbohydratesGrams] = useState("");
  const [reasonForChange, setReasonForChange] = useState("");

  const [caloriesError, setCaloriesError] = useState(null);
  const [reasonForChangeError, setReasonForChangeError] = useState(null);

  useEffect(() => {
    if (defaultTargets) {
      setCalories(defaultTargets.calories.toString());
      setProteinPercentage(defaultTargets.proteinPercentage);
      setProteinGrams(
        defaultTargets.proteinGrams?.toString() ||
          defaultTargets.proteinValue?.toString()
      );
      setCarbohydratesPercentage(
        defaultTargets.carbohydratesPercentage || defaultTargets.carbsPercentage
      );
      setCarbohydratesGrams(
        defaultTargets.carbohydratesGrams?.toString() ||
          defaultTargets.carbsValue?.toString()
      );
    } else {
      setProteinPercentage(25);
      setCarbohydratesPercentage(50);
    }
  }, [defaultTargets]);

  const validateForm = useCallback(() => {
    let hasErrorsCalories = false;
    let hasErrorsReason = false;
    setCaloriesError(null);
    setReasonForChangeError(null);
    if (!calories) {
      setCaloriesError("Please fill the input");
      hasErrorsCalories = true;
    }

    if (editing && reasonForChange.replace(/\.|,/g, "").trim() === "") {
      setReasonForChangeError("Please provide a reason");
      hasErrorsReason = true;
    }

    if (editing && reasonForChange.length > 450) {
      setReasonForChangeError(
        "Please enter a reason with less than 450 characters"
      );
      hasErrorsReason = true;
    }

    onValidate(!hasErrorsCalories && !hasErrorsReason, {
      calories: Number(calories),
      proteinPercentage,
      proteinGrams: Number(proteinGrams),
      carbohydratesPercentage,
      carbohydratesGrams: Number(carbohydratesGrams),
      reasonForChange,
    });
  }, [
    calories,
    carbohydratesGrams,
    carbohydratesPercentage,
    editing,
    onValidate,
    proteinGrams,
    proteinPercentage,
    reasonForChange,
  ]);

  const onProteinChanged = (percentage, grams) => {
    setProteinPercentage(percentage);
    setProteinGrams(grams);
  };

  const onCarbohydratesChanged = (percentage, grams) => {
    setCarbohydratesPercentage(percentage);
    setCarbohydratesGrams(grams);
  };

  useEffect(() => {
    if (validate) {
      validateForm();
    }
  }, [validate, validateForm]);

  return (
    <Container color={backgroundColor} data-testid="setdailygoals">
      <Title>Set daily targets for this participant</Title>

      <LabelledInput
        data-testid="calories-input"
        className="mt-4"
        title="Calories"
        value={calories}
        pattern="[0-9]{0,4}"
        min="0"
        type="text"
        hasError={caloriesError !== null}
        errorMessage={caloriesError}
        onChange={(e) => e.target.validity.valid && setCalories(e.target.value)}
      />

      <PercentageInput
        className="mt-4"
        title="Protein"
        calories={calories === "" ? "0" : calories}
        magnitude="≥"
        defaultValue={proteinPercentage?.toString()}
        kCalGramsRelation={4}
        info="* Protocol target: Greater than or equal to 25% of total calories"
        onChange={onProteinChanged}
      />

      <PercentageInput
        className="mt-4"
        title="Carbohydrates"
        calories={calories === "" ? "0" : calories}
        magnitude="<"
        defaultValue={carbohydratesPercentage?.toString()}
        kCalGramsRelation={4}
        info="* Protocol target: Less than 50% of total calories"
        onChange={onCarbohydratesChanged}
      />

      {editing && (
        <LabelledInput
          data-testid="reason-change-input"
          className="mt-4"
          title="Reason for change:"
          value={reasonForChange}
          min="0"
          type="text"
          hasError={reasonForChangeError !== null}
          errorMessage={reasonForChangeError}
          onChange={(e) =>
            e.target.validity.valid && setReasonForChange(e.target.value)
          }
        />
      )}
    </Container>
  );
};

SetDailyGoals.defaultProps = {
  backgroundColor: null,
  validate: false,
  onValidate: null,
  editing: false,
  caloriesProp: null,
  carbohydratesGramsProp: null,
  carbohydratesPercentageProp: null,
  proteinGramsProp: null,
  proteinPercentageProp: null,
};

SetDailyGoals.propTypes = {
  backgroundColor: PropTypes.string,
  validate: PropTypes.bool,
  onValidate: PropTypes.func,
  editing: PropTypes.bool,
};

export default withTheme(SetDailyGoals);
